/**
 * The ad-attribution and funnel-id contracts shared by the ffs-signup SPA (which captures them) and
 * the ffs-signup service (which accepts them on funnel requests). One schema each, so the two cannot
 * drift: a value the SPA builds and a schema here rejects is silently dropped by the service.
 *
 * `utm` values are free-form and may carry identifiers (email tools sometimes put an address in
 * `utm_term`). They are captured as-is but are NEVER forwarded off-platform: the ad-conversions
 * consumer's input transformer drops `utm` entirely before anything leaves the platform.
 */

import { z } from 'zod';

export const AD_ATTRIBUTION_UTM_KEYS = ['source', 'medium', 'campaign', 'content', 'term'] as const;

/** Precedence order: Google accepts exactly one of its three, and one real click carries one id. */
export const AD_ATTRIBUTION_CLICK_TYPES = ['gclid', 'gbraid', 'wbraid', 'fbclid'] as const;

/** Tested after trimming. */
export const AD_ATTRIBUTION_UTM_RE = /^[\x20-\x7E]{1,200}$/;
/** Tested after trimming. */
export const AD_ATTRIBUTION_NRX_LP_RE = /^[a-z0-9-]{1,64}$/;
export const AD_ATTRIBUTION_CLICK_ID_RE = /^[A-Za-z0-9_-]{1,512}$/;
export const AD_ATTRIBUTION_LANDING_PATH_RE = /^\/[^?#\s]{0,199}$/;
export const AD_ATTRIBUTION_FBC_RE = /^fb\.1\.\d{13}\.[A-Za-z0-9_-]{1,512}$/;

const utmValue = z.string().trim().regex(AD_ATTRIBUTION_UTM_RE);

export const AdAttributionSchema = z.strictObject({
  v: z.literal(1),
  capturedAt: z.iso.datetime(),
  landingPath: z.string().regex(AD_ATTRIBUTION_LANDING_PATH_RE).exactOptional(),
  nrxLp: z.string().trim().regex(AD_ATTRIBUTION_NRX_LP_RE).exactOptional(),
  utm: z
    .strictObject({
      source: utmValue.exactOptional(),
      medium: utmValue.exactOptional(),
      campaign: utmValue.exactOptional(),
      content: utmValue.exactOptional(),
      term: utmValue.exactOptional(),
    })
    .exactOptional(),
  click: z
    .strictObject({
      type: z.enum(AD_ATTRIBUTION_CLICK_TYPES),
      id: z.string().regex(AD_ATTRIBUTION_CLICK_ID_RE),
      fbc: z.string().regex(AD_ATTRIBUTION_FBC_RE).exactOptional(),
    })
    .exactOptional(),
});

/** How a member arrived: the last ad touch the SPA captured. Non-clinical; never routes a member. */
export type FfsAdAttribution = z.infer<typeof AdAttributionSchema>;

/** The attribution if `raw` satisfies the contract, else `undefined`. Never throws, never logs. */
export function parseAdAttribution(raw: unknown): FfsAdAttribution | undefined {
  const parsed = AdAttributionSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}

/**
 * The visitor funnel id: a random v4 UUID the SPA mints per visitor (30-day life) and sends on the
 * eligibility check, enrollment and consult-booked calls, so the analytics sink can join a check to
 * the enrollment that follows. Random, so it says nothing about the person; lowercase because
 * `crypto.randomUUID()` is. Never written to FHIR; the ad-conversions consumer (later) must never
 * forward it.
 */
export const FUNNEL_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

export const FunnelIdSchema = z.string().regex(FUNNEL_ID_RE);

/** The funnel id if `raw` is one, else `undefined`. Never throws, never logs. */
export function parseFunnelId(raw: unknown): string | undefined {
  const parsed = FunnelIdSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}
